<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import AutomationList from './components/automations/AutomationList.vue';
import AutomationEditor from './components/automations/AutomationEditor.vue';
import KanbanImportModal from './components/KanbanImportModal.vue';
import KanbanTrashList from './components/KanbanTrashList.vue';
import PipelineDeleteModal from './PipelineDeleteModal.vue';
import ColumnDeleteModal from './ColumnDeleteModal.vue';
import { useAlert } from 'dashboard/composables';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const activePipeline = computed(() => store.getters['kanban/activePipeline']);

// --- Abas ---
const activeTab = ref('stages'); // 'stages' | 'automations' | 'import' | 'trash'
const editingAutomation = ref(null); // null = lista; objeto/{} = editor
function openNewAutomation() {
  editingAutomation.value = {};
}
function openEditAutomation(automation) {
  editingAutomation.value = automation;
}
function closeEditor() {
  editingAutomation.value = null;
}
const columns = ref([]);
const moveWonToLastColumn = ref(false);
const category = ref('sales');
const pipelineName = ref('');
const saveStatus = ref(''); // '' | 'saving' | 'saved'
let saveTimer = null;
let statusTimer = null;
let initialized = false;

watch(
  activePipeline,
  pipeline => {
    if (pipeline) {
      initialized = false;
      columns.value = pipeline.columns.map(c => ({ ...c }));
      moveWonToLastColumn.value = pipeline.move_won_to_last_column || false;
      category.value = pipeline.category || 'sales';
      pipelineName.value = pipeline.name || '';
      setTimeout(() => {
        initialized = true;
      }, 0);
    }
  },
  { immediate: true }
);

function scheduleSave() {
  if (!initialized) return;
  saveStatus.value = 'saving';
  clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    try {
      // eslint-disable-next-line no-use-before-define
      await saveAll();
    } catch (errors) {
      saveStatus.value = '';
      useAlert(
        (Array.isArray(errors) && errors[0]) || t('KANBAN.SETTINGS.SAVE_ERROR')
      );
      // o backend recusou o save inteiro (ex.: etapa removida ainda com cards);
      // volta a tela para o estado real do funil em vez de manter a etapa sumida
      // eslint-disable-next-line no-use-before-define
      pendingReassignments.value = {};
      store.dispatch('kanban/fetchPipelines');
      return;
    }
    saveStatus.value = 'saved';
    clearTimeout(statusTimer);
    statusTimer = setTimeout(() => {
      saveStatus.value = '';
    }, 2000);
  }, 800);
}

onUnmounted(() => {
  clearTimeout(saveTimer);
  clearTimeout(statusTimer);
});

watch([pipelineName, category, moveWonToLastColumn], scheduleSave, {
  deep: true,
});
watch(columns, scheduleSave, { deep: true });

// --- Drag and drop de colunas ---
const dragSrcIdx = ref(null);
const dragOverIdx = ref(null);

function onDragStart(idx) {
  dragSrcIdx.value = idx;
}

function onDragOver(e, idx) {
  e.preventDefault();
  dragOverIdx.value = idx;
}

function onDrop(idx) {
  if (dragSrcIdx.value === null || dragSrcIdx.value === idx) {
    dragSrcIdx.value = null;
    dragOverIdx.value = null;
    return;
  }
  const moved = columns.value.splice(dragSrcIdx.value, 1)[0];
  columns.value.splice(idx, 0, moved);
  columns.value.forEach((c, i) => {
    c.position = i;
  });
  dragSrcIdx.value = null;
  dragOverIdx.value = null;
  // eslint-disable-next-line no-use-before-define
  expandedWebhookIdx.value = null;
  store.commit('kanban/UPDATE_PIPELINE_COLUMNS', {
    pipelineId: activePipeline.value.id,
    columns: columns.value.map(c => ({ ...c })),
  });
}

function onDragEnd() {
  dragSrcIdx.value = null;
  dragOverIdx.value = null;
}

// --- Colunas ---
// índice da coluna com o campo de webhook expandido (null = nenhum)
const expandedWebhookIdx = ref(null);

function toggleWebhook(idx) {
  expandedWebhookIdx.value = expandedWebhookIdx.value === idx ? null : idx;
}

function addColumn() {
  columns.value.push({
    id: null,
    name: t('KANBAN.SETTINGS.NEW_COLUMN'),
    position: columns.value.length,
    column_type: 'default',
  });
}

// coluna aguardando o fluxo de exclusão em 2 etapas (null = modal fechado)
const deletingColumnIdx = ref(null);

const deletingColumn = computed(() =>
  deletingColumnIdx.value === null
    ? null
    : columns.value[deletingColumnIdx.value]
);

// contagem real vinda do backend: o store só tem os cards do filtro ativo, então
// contar localmente subestimaria quantas oportunidades serão movidas. Enquanto o
// backend novo não estiver no ar, cards_count vem undefined — cair para a contagem
// local evita o pior caso (0 = pular a escolha de destino e apagar os cards).
const deletingColumnCardCount = computed(() => {
  const column = deletingColumn.value;
  if (!column?.id) return 0;
  if (Number.isInteger(column.cards_count)) return column.cards_count;
  return (store.getters['kanban/cardsByColumn'](column.id) ?? []).length;
});

// destinos possíveis: demais colunas já persistidas
const targetColumns = computed(() =>
  columns.value.filter((c, i) => c.id && i !== deletingColumnIdx.value)
);

function discardColumn(idx) {
  columns.value.splice(idx, 1);
  columns.value.forEach((c, i) => {
    c.position = i;
  });
  expandedWebhookIdx.value = null;
}

function removeColumn(idx) {
  const col = columns.value[idx];
  // coluna nunca salva não tem cards nem exige confirmação
  if (!col?.id) {
    discardColumn(idx);
    return;
  }
  deletingColumnIdx.value = idx;
}

// { <id da coluna removida>: <id da coluna destino> } — enviado no próximo save para
// que o backend realoque os cards antes do destroy, sobre o conjunto completo.
const pendingReassignments = ref({});

function confirmRemoveColumn(targetColumnId) {
  const idx = deletingColumnIdx.value;
  const removedId = deletingColumn.value?.id;

  if (targetColumnId && removedId) {
    pendingReassignments.value = {
      ...pendingReassignments.value,
      [removedId]: targetColumnId,
    };
  }

  deletingColumnIdx.value = null;
  discardColumn(idx);
}

async function saveAll() {
  const reassignments = pendingReassignments.value;
  await store.dispatch('kanban/updatePipeline', {
    id: activePipeline.value.id,
    name: pipelineName.value || activePipeline.value.name,
    move_won_to_last_column: moveWonToLastColumn.value,
    columns: columns.value,
    ...(Object.keys(reassignments).length
      ? { column_reassignments: reassignments }
      : {}),
  });
  // só limpa o que foi efetivamente enviado — uma exclusão feita durante o await
  // precisa sobreviver para o próximo save.
  const remaining = { ...pendingReassignments.value };
  Object.keys(reassignments).forEach(key => delete remaining[key]);
  pendingReassignments.value = remaining;
}

const showDeleteModal = ref(false);

async function confirmDeletePipeline() {
  try {
    await store.dispatch('kanban/deletePipeline', activePipeline.value.id);
    useAlert(t('KANBAN.SETTINGS.DELETE_PIPELINE_SUCCESS'));
    showDeleteModal.value = false;
    emit('close');
  } catch {
    useAlert(t('KANBAN.SETTINGS.DELETE_PIPELINE_ERROR'));
  }
}
</script>

<template>
  <div class="flex flex-col h-full min-h-0">
    <!-- Header -->
    <div
      class="flex items-center justify-between gap-2 px-4 py-3 border-b border-n-weak flex-shrink-0"
    >
      <input
        v-model="pipelineName"
        class="text-sm font-semibold text-n-slate-12 bg-transparent border-b border-transparent hover:border-n-weak focus:border-n-brand focus:outline-none transition-colors px-0.5 min-w-0 flex-1"
      />

      <div class="flex items-center gap-2 flex-shrink-0">
        <span
          :title="$t('KANBAN.SETTINGS.CATEGORY_LOCKED')"
          class="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-n-surface-2 text-n-slate-12 border border-n-weak cursor-not-allowed"
        >
          <span
            class="size-3.5"
            :class="
              category === 'sales'
                ? 'i-lucide-trending-up text-n-teal-9'
                : 'i-lucide-headset text-n-amber-10'
            "
          />
          {{
            category === 'sales'
              ? $t('KANBAN.SETTINGS.CATEGORY_SALES')
              : $t('KANBAN.SETTINGS.CATEGORY_POST_SALES')
          }}
        </span>
        <button
          class="text-n-slate-10 hover:text-n-slate-12 transition-colors"
          @click="emit('close')"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>
    </div>

    <!-- Abas -->
    <div
      class="flex items-center gap-0.5 px-2 border-b border-n-weak flex-shrink-0"
    >
      <button
        class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px"
        :class="
          activeTab === 'stages'
            ? 'border-n-brand text-n-slate-12'
            : 'border-transparent text-n-slate-10 hover:text-n-slate-12'
        "
        @click="activeTab = 'stages'"
      >
        <span class="i-lucide-columns-3 size-4" />
        {{ $t('KANBAN.AUTOMATIONS.TAB_STAGES') }}
      </button>
      <button
        class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px"
        :class="
          activeTab === 'automations'
            ? 'border-n-brand text-n-slate-12'
            : 'border-transparent text-n-slate-10 hover:text-n-slate-12'
        "
        @click="activeTab = 'automations'"
      >
        <span class="i-lucide-bot size-4" />
        {{ $t('KANBAN.AUTOMATIONS.TITLE') }}
      </button>
      <button
        class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px"
        :class="
          activeTab === 'import'
            ? 'border-n-brand text-n-slate-12'
            : 'border-transparent text-n-slate-10 hover:text-n-slate-12'
        "
        @click="activeTab = 'import'"
      >
        <span class="i-lucide-upload size-4" />
        {{ $t('KANBAN.IMPORT.BUTTON_LABEL') }}
      </button>
      <button
        class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px"
        :class="
          activeTab === 'trash'
            ? 'border-n-brand text-n-slate-12'
            : 'border-transparent text-n-slate-10 hover:text-n-slate-12'
        "
        @click="activeTab = 'trash'"
      >
        <span class="i-lucide-trash-2 size-4" />
        {{ $t('KANBAN.TRASH.TAB') }}
      </button>
    </div>

    <!-- Corpo rolável -->
    <div class="flex-1 overflow-y-auto p-4 min-h-0">
      <!-- Aba: Importar -->
      <KanbanImportModal v-if="activeTab === 'import'" model-value embedded />

      <!-- Aba: Lixeira -->
      <KanbanTrashList
        v-if="activeTab === 'trash' && activePipeline"
        :pipeline-id="activePipeline.id"
      />

      <!-- Aba: Automações -->
      <div v-if="activeTab === 'automations' && activePipeline">
        <AutomationList
          v-if="!editingAutomation"
          :pipeline-id="activePipeline.id"
          @create="openNewAutomation"
          @edit="openEditAutomation"
        />
        <AutomationEditor
          v-else
          :pipeline-id="activePipeline.id"
          :automation="editingAutomation.id ? editingAutomation : null"
          @saved="closeEditor"
          @cancel="closeEditor"
        />
      </div>

      <!-- Aba: Etapas -->
      <div
        v-if="activeTab === 'stages' && activePipeline"
        class="flex flex-col gap-2"
      >
        <div
          v-for="(col, idx) in columns"
          :key="col.id ?? idx"
          class="rounded-lg border border-n-weak bg-[#FEFEFE] dark:!bg-[#141517] px-2 py-1.5 transition-all"
          :class="{
            'opacity-40': dragSrcIdx === idx,
            'ring-2 ring-n-brand': dragOverIdx === idx && dragSrcIdx !== idx,
          }"
          draggable="true"
          @dragstart="onDragStart(idx)"
          @dragover="onDragOver($event, idx)"
          @drop="onDrop(idx)"
          @dragend="onDragEnd"
        >
          <div class="flex items-center gap-2">
            <span
              class="text-n-slate-10 cursor-grab select-none flex-shrink-0"
              aria-hidden="true"
            >
              {{ $t('KANBAN.SETTINGS.DRAG_HANDLE') }}
            </span>
            <input
              v-model="col.name"
              class="flex-1 min-w-0 text-sm px-1 py-1 bg-transparent text-n-slate-12 border-none focus:outline-none focus:ring-0"
            />
            <button
              class="flex-shrink-0 p-1 rounded-md hover:bg-n-alpha-1 transition-colors"
              :class="
                col.webhook_url || expandedWebhookIdx === idx
                  ? 'text-n-brand'
                  : 'text-n-slate-9 hover:text-n-slate-11'
              "
              :aria-label="$t('KANBAN.SETTINGS.WEBHOOK_TOGGLE')"
              :title="$t('KANBAN.SETTINGS.WEBHOOK_TOGGLE')"
              @click="toggleWebhook(idx)"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                />
                <path
                  d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                />
              </svg>
            </button>
            <button
              class="flex-shrink-0 p-1 rounded-md text-n-slate-9 hover:text-n-ruby-11 hover:bg-n-alpha-1 transition-colors"
              :aria-label="$t('KANBAN.SETTINGS.REMOVE')"
              :title="$t('KANBAN.SETTINGS.REMOVE')"
              @click="removeColumn(idx)"
            >
              <span class="i-lucide-trash-2 size-3.5 block" />
            </button>
          </div>
          <input
            v-if="expandedWebhookIdx === idx"
            v-model="col.webhook_url"
            type="text"
            :placeholder="$t('KANBAN.SETTINGS.WEBHOOK_PLACEHOLDER')"
            class="w-full text-xs rounded-lg px-3 py-2 mt-1.5 mb-0.5 bg-n-surface-2 border border-transparent text-n-slate-11 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand transition-colors"
          />
        </div>
        <button
          class="text-sm text-n-brand hover:opacity-80 font-medium self-start mt-1"
          @click="addColumn"
        >
          {{ $t('KANBAN.SETTINGS.ADD_COLUMN') }}
        </button>
      </div>
    </div>

    <!-- Rodapé fixo -->
    <div
      v-if="activeTab === 'stages'"
      class="flex flex-col gap-3 px-4 py-3 border-t border-n-weak flex-shrink-0"
    >
      <label class="flex items-center gap-2 text-sm text-n-slate-11">
        <input v-model="moveWonToLastColumn" type="checkbox" class="rounded" />
        {{ $t('KANBAN.SETTINGS.MOVE_WON_TO_LAST') }}
      </label>

      <div class="flex items-center justify-between gap-2">
        <button
          type="button"
          class="flex items-center gap-1.5 text-sm text-n-ruby-11 hover:opacity-80 font-medium"
          @click="showDeleteModal = true"
        >
          <span class="i-lucide-trash-2 size-3.5" />
          {{ $t('KANBAN.SETTINGS.DELETE_PIPELINE') }}
        </button>

        <transition name="fade">
          <span v-if="saveStatus" class="text-xs text-n-slate-10">
            {{
              saveStatus === 'saving'
                ? $t('KANBAN.SETTINGS.SAVING')
                : $t('KANBAN.SETTINGS.SAVED')
            }}
          </span>
        </transition>
      </div>
    </div>

    <ColumnDeleteModal
      v-if="deletingColumn"
      :column="deletingColumn"
      :target-columns="targetColumns"
      :card-count="deletingColumnCardCount"
      @confirm="confirmRemoveColumn"
      @cancel="deletingColumnIdx = null"
    />

    <PipelineDeleteModal
      v-if="showDeleteModal && activePipeline"
      :pipeline="activePipeline"
      @confirm="confirmDeletePipeline"
      @cancel="showDeleteModal = false"
    />
  </div>
</template>
